import {useMemo} from 'react';

import {useProjectParams} from '../../../pages/ProjectPage/useProjectParams';
import {useEntityDynamicConfigWithLoading} from '../../../util/useDynamicConfig';

export const DEFAULT_GROUPING_AGGREGATION_LIMIT = 10_000;

// Statsig dynamic config and JSON key that carry the server-side cap. Must stay
// in sync with DynamicConfigMaxRunsCountedInRunSelector and
// runSelectorGroupMaxRunsSettings.MaxRuns in
// services/gorilla/api/resolver/ramps.go.
const MAX_RUNS_CONFIG_ID = 'max_runs_counted_grouping_in_run_selector';
const MAX_RUNS_CONFIG_KEY = 'max_runs';

/**
 * Max number of runs counted per grouped run-selector row on the lean
 * grouped-runs query path. The authoritative cap is applied server-side via
 * the Statsig dynamic config `max_runs_counted_grouping_in_run_selector`
 *
 * This hook reads the same config at the project's entity level (the dimension
 * the backend targets on) so the displayed cap (e.g. the RunsCountTag warning)
 * matches what the server actually applies. When the config is unset or the
 * entity is unavailable, it falls back to the shared default.
 */
export function useGroupingAggregationLimit(): number {
  const {entityName} = useProjectParams();
  // `loading` is intentionally unused: while the config loads, `config` is
  // undefined and the hook reports the default cap, which is the correct
  // value for most entities (the config is unset). An entity with a raised
  // cap may briefly see the default in the count label until the config
  // resolves; gating on `loading` instead would blank the label for everyone.
  const {config} = useEntityDynamicConfigWithLoading(
    MAX_RUNS_CONFIG_ID,
    entityName ?? ''
  );

  return useMemo(() => {
    const rawMaxRuns = (config as Record<string, unknown> | undefined)?.[
      MAX_RUNS_CONFIG_KEY
    ];
    if (typeof rawMaxRuns === 'number' && rawMaxRuns > 0) {
      return rawMaxRuns;
    }
    return DEFAULT_GROUPING_AGGREGATION_LIMIT;
  }, [config]);
}
